/**
 * Game Center Integration Module
 * 
 * Provides integration with Apple Game Center for:
 * - Player authentication
 * - Leaderboard score submission
 * - Achievement tracking
 * - Player profile access
 */

import { Capacitor } from '@capacitor/core';

export interface GameCenterPlayer {
    playerId: string;
    displayName: string;
    alias: string;
}

export interface LeaderboardScore {
    value: number;
    context: number;
    rank?: number;
    formattedValue?: string;
}

export interface Achievement {
    identifier: string;
    percentComplete: number;
    completed: boolean;
    lastReportedDate?: Date;
}

/**
 * Game Center service wrapper
 * Handles all interactions with Apple's Game Center API
 */
export class GameCenterService {
    private static instance: GameCenterService;
    private isAvailable: boolean = false;
    private isAuthenticated: boolean = false;
    private currentPlayer: GameCenterPlayer | null = null;
    private plugin: any = null;
    private initializationPromise: Promise<void> | null = null;

    // Leaderboard identifiers (configure these in App Store Connect)
    public static readonly LEADERBOARDS = {
        ENDLESS_HIGH_SCORE: 'com.shundor.sctgame.leaderboard.highscore', // Endless mode high score (existing leaderboard)
        TIME_ATTACK_HIGH_SCORE: 'com.shundor.sctgame.leaderboard.timeattack.highscore', // Time Attack mode high score (new leaderboard)
        LONGEST_CHAIN: 'com.shundor.sctgame.leaderboard.longestchain',
        TOTAL_TIME: 'com.shundor.sctgame.leaderboard.totaltime',
        GAMES_PLAYED: 'com.shundor.sctgame.leaderboard.gamesplayed'
    };

    // Achievement identifiers (configure these in App Store Connect)
    public static readonly ACHIEVEMENTS = {
        FIRST_GAME: 'com.shundor.sctgame.achievement.firstgame',
        SCORE_1000: 'com.shundor.sctgame.achievement.score1000',
        SCORE_5000: 'com.shundor.sctgame.achievement.score5000',
        SCORE_10000: 'com.shundor.sctgame.achievement.score10000',
        CHAIN_5: 'com.shundor.sctgame.achievement.chain5',
        CHAIN_10: 'com.shundor.sctgame.achievement.chain10',
        CHAIN_15: 'com.shundor.sctgame.achievement.chain15',
        GAMES_10: 'com.shundor.sctgame.achievement.games10',
        GAMES_50: 'com.shundor.sctgame.achievement.games50',
        GAMES_100: 'com.shundor.sctgame.achievement.games100',
        HOUR_PLAYED: 'com.shundor.sctgame.achievement.hour',
        TEN_HOURS_PLAYED: 'com.shundor.sctgame.achievement.tenhours'
    };

    private constructor() {
        // Don't call initialize here - it will be called explicitly
    }

    public static getInstance(): GameCenterService {
        if (!GameCenterService.instance) {
            GameCenterService.instance = new GameCenterService();
            // Start initialization immediately
            GameCenterService.instance.initializationPromise = GameCenterService.instance.initialize().catch(err => {
                console.warn('Game Center initialization failed:', err);
            });
        }
        return GameCenterService.instance;
    }

    /**
     * Wait for initialization to complete
     */
    public async waitForInitialization(): Promise<void> {
        if (this.initializationPromise) {
            await this.initializationPromise;
        }
    }

    /**
     * Initialize Game Center
     */
    private async initialize(): Promise<void> {
        console.log('Game Center: Starting initialization...');
        console.log('Platform:', Capacitor.getPlatform());
        console.log('Is native platform:', Capacitor.isNativePlatform());

        // Check if we're on iOS native platform
        if (Capacitor.getPlatform() !== 'ios' || !Capacitor.isNativePlatform()) {
            console.log('Game Center not available - not on iOS native platform');
            this.isAvailable = false;
            return;
        }

        // Access plugin from Capacitor.Plugins registry (registered by native plugin)
        try {
            // Check if the plugin is registered with Capacitor
            const plugins = (Capacitor as any).Plugins;
            if (plugins && plugins.GameCenter) {
                this.plugin = plugins.GameCenter;
                this.isAvailable = true;
                console.log('Game Center plugin loaded successfully from Capacitor.Plugins');

                // Auto-authenticate on initialization
                try {
                    await this.authenticatePlayer();
                } catch (error) {
                    console.warn('Game Center auto-authentication failed:', error);
                }
            } else {
                console.log('Game Center plugin not registered in Capacitor.Plugins');
                this.isAvailable = false;
            }
        } catch (error) {
            console.warn('Failed to access Game Center plugin:', error);
            this.isAvailable = false;
            this.plugin = null;
        }
    }    /**
     * Check if Game Center is available on this device
     */
    public isGameCenterAvailable(): boolean {
        return this.isAvailable;
    }

    /**
     * Check if player is authenticated
     */
    public isPlayerAuthenticated(): boolean {
        return this.isAuthenticated;
    }

    /**
     * Get current player info
     */
    public getCurrentPlayer(): GameCenterPlayer | null {
        return this.currentPlayer;
    }

    /**
     * Authenticate the player with Game Center
     * This will show the Game Center login UI if needed
     */
    public async authenticatePlayer(): Promise<boolean> {
        if (!this.isAvailable || !this.plugin) {
            console.log('Game Center not available');
            return false;
        }

        try {
            const result = await this.plugin.authenticate();
            this.isAuthenticated = result.authenticated;

            if (result.authenticated && result.playerId) {
                this.currentPlayer = {
                    playerId: result.playerId,
                    displayName: result.displayName || '',
                    alias: result.alias || ''
                };
            }

            console.log('Game Center authentication:', result.authenticated ? 'success' : 'failed');
            return this.isAuthenticated;
        } catch (error) {
            console.error('Game Center authentication error:', error);
            this.isAuthenticated = false;
            return false;
        }
    }

    /**
     * Submit a score to a leaderboard
     */
    public async submitScore(leaderboardId: string, score: number): Promise<boolean> {
        if (!this.isAuthenticated || !this.plugin) {
            console.log('Cannot submit score - player not authenticated');
            return false;
        }

        try {
            await this.plugin.submitScore({ leaderboardId, score });
            console.log(`Score submitted to Game Center: ${leaderboardId} = ${score}`);
            return true;
        } catch (error) {
            console.error('Error submitting score to Game Center:', error);
            return false;
        }
    }

    /**
     * Submit high score to leaderboard
     * @deprecated Use submitEndlessHighScore or submitTimeAttackHighScore instead
     */
    public async submitHighScore(score: number): Promise<boolean> {
        // Default to Endless mode for backwards compatibility
        return this.submitScore(GameCenterService.LEADERBOARDS.ENDLESS_HIGH_SCORE, score);
    }

    /**
     * Submit Endless mode high score to leaderboard
     */
    public async submitEndlessHighScore(score: number): Promise<boolean> {
        // Submit to the existing Endless leaderboard (keeps existing tester data)
        return this.submitScore(GameCenterService.LEADERBOARDS.ENDLESS_HIGH_SCORE, score);
    }

    /**
     * Submit Time Attack mode high score to leaderboard
     */
    public async submitTimeAttackHighScore(score: number): Promise<boolean> {
        return this.submitScore(GameCenterService.LEADERBOARDS.TIME_ATTACK_HIGH_SCORE, score);
    }

    /**
     * Submit longest chain to leaderboard
     */
    public async submitLongestChain(chain: number): Promise<boolean> {
        return this.submitScore(GameCenterService.LEADERBOARDS.LONGEST_CHAIN, chain);
    }

    /**
     * Submit total time played to leaderboard (in seconds)
     */
    public async submitTotalTime(timeInSeconds: number): Promise<boolean> {
        return this.submitScore(GameCenterService.LEADERBOARDS.TOTAL_TIME, timeInSeconds);
    }

    /**
     * Submit games played count to leaderboard
     */
    public async submitGamesPlayed(count: number): Promise<boolean> {
        return this.submitScore(GameCenterService.LEADERBOARDS.GAMES_PLAYED, count);
    }

    /**
     * Report achievement progress
     */
    public async reportAchievement(achievementId: string, percentComplete: number): Promise<boolean> {
        if (!this.isAuthenticated || !this.plugin) {
            console.log('Cannot report achievement - player not authenticated');
            return false;
        }

        try {
            await this.plugin.reportAchievement({ achievementId, percentComplete });
            console.log(`Achievement reported: ${achievementId} at ${percentComplete}%`);
            return true;
        } catch (error) {
            console.error('Error reporting achievement to Game Center:', error);
            return false;
        }
    }

    /**
     * Check and report score-based achievements
     */
    public async checkScoreAchievements(score: number): Promise<void> {
        if (score >= 1000) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.SCORE_1000, 100);
        }
        if (score >= 5000) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.SCORE_5000, 100);
        }
        if (score >= 10000) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.SCORE_10000, 100);
        }
    }

    /**
     * Check and report chain-based achievements
     */
    public async checkChainAchievements(chain: number): Promise<void> {
        if (chain >= 5) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.CHAIN_5, 100);
        }
        if (chain >= 10) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.CHAIN_10, 100);
        }
        if (chain >= 15) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.CHAIN_15, 100);
        }
    }

    /**
     * Check and report games played achievements
     */
    public async checkGamesPlayedAchievements(gamesPlayed: number): Promise<void> {
        if (gamesPlayed === 1) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.FIRST_GAME, 100);
        }
        if (gamesPlayed >= 10) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.GAMES_10, 100);
        }
        if (gamesPlayed >= 50) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.GAMES_50, 100);
        }
        if (gamesPlayed >= 100) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.GAMES_100, 100);
        }
    }

    /**
     * Check and report time-based achievements
     */
    public async checkTimeAchievements(totalSeconds: number): Promise<void> {
        const hours = totalSeconds / 3600;

        if (hours >= 1) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.HOUR_PLAYED, 100);
        }
        if (hours >= 10) {
            await this.reportAchievement(GameCenterService.ACHIEVEMENTS.TEN_HOURS_PLAYED, 100);
        }
    }

    /**
     * Show the Game Center leaderboard UI
     */
    public async showLeaderboard(leaderboardId?: string): Promise<void> {
        if (!this.isAuthenticated || !this.plugin) {
            console.log('Cannot show leaderboard - player not authenticated');
            return;
        }

        try {
            await this.plugin.showLeaderboard(leaderboardId ? { leaderboardId } : undefined);
            console.log(`Showing Game Center leaderboard: ${leaderboardId || 'all'}`);
        } catch (error) {
            console.error('Error showing leaderboard:', error);
        }
    }

    /**
     * Show the Game Center achievements UI
     */
    public async showAchievements(): Promise<void> {
        if (!this.isAuthenticated || !this.plugin) {
            console.log('Cannot show achievements - player not authenticated');
            return;
        }

        try {
            await this.plugin.showAchievements();
            console.log('Showing Game Center achievements');
        } catch (error) {
            console.error('Error showing achievements:', error);
        }
    }

    /**
     * Reset all achievements (for testing only - not available in production)
     */
    public async resetAchievements(): Promise<void> {
        if (!this.isAuthenticated || !this.plugin) {
            console.log('Cannot reset achievements - player not authenticated');
            return;
        }

        try {
            // TODO: Call native plugin method when available
            // This should only work in development/sandbox mode
            // await GameCenter.resetAchievements();

            console.log('Resetting Game Center achievements (dev only)');

            if (Capacitor.isNativePlatform()) {
                console.warn('Game Center resetAchievements native bridge not yet implemented');
            }
        } catch (error) {
            console.error('Error resetting achievements:', error);
        }
    }

    /**
     * Get player's achievements
     */
    public async getAchievements(): Promise<Achievement[]> {
        if (!this.isAuthenticated || !this.plugin) {
            console.log('Cannot get achievements - player not authenticated');
            return [];
        }

        try {
            const result = await this.plugin.getAchievements();
            return result.achievements.map((a: any) => ({
                ...a,
                lastReportedDate: a.lastReportedDate ? new Date(a.lastReportedDate) : undefined
            }));
        } catch (error) {
            console.error('Error getting achievements:', error);
            return [];
        }
    }

    /**
     * Get player's scores for a leaderboard
     */
    public async getScores(leaderboardId: string): Promise<LeaderboardScore[]> {
        if (!this.isAuthenticated || !this.plugin) {
            console.log('Cannot get scores - player not authenticated');
            return [];
        }

        try {
            const result = await this.plugin.getScores({ leaderboardId });
            return result.scores;
        } catch (error) {
            console.error('Error getting scores:', error);
            return [];
        }
    }
}

// Export singleton instance
export const GameCenter = GameCenterService.getInstance();
